// Paket P1 — PromoCta.
//
// Gradient-Knopf (Fackelgold → Glut) mit dezentem Schimmer. Rendert je
// nach Props einen <Button>, ein <Link> (react-router) oder einen
// <a>-Tag (extern). Tap-Target ≥ 44 px (Design-System §0).
//
// Animation: goldener Schimmer wandert ueber die Flaeche (6–8 s),
// ausgeschaltet unter `prefers-reduced-motion`.

import type { CSSProperties, MouseEvent, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { cx } from '../ui/classNames';
import type { TierMarkTier } from './TierMark';
import styles from './PromoCta.module.css';

export interface PromoCtaCommonProps {
  children: ReactNode;
  /** Stufe (faerbt den Knopf in der Stufen-Akzent-Farbe). Default Fackelgold. */
  tier?: TierMarkTier;
  loading?: boolean;
  disabled?: boolean;
  /** "solid" (Default) oder "ghost" (durchsichtiger Rand). */
  variant?: 'solid' | 'ghost';
  /** Zusätzliche Klasse (z. B. für volle Breite). */
  className?: string;
  fullWidth?: boolean;
  /** data-testid wird 1:1 auf den Knopf durchgereicht (für e2e und Tests). */
  'data-testid'?: string;
}

export type PromoCtaProps =
  | (PromoCtaCommonProps & { onClick: (event: MouseEvent<HTMLButtonElement>) => void; to?: undefined; href?: undefined })
  | (PromoCtaCommonProps & { to: string; onClick?: undefined; href?: undefined })
  | (PromoCtaCommonProps & { href: string; onClick?: undefined; to?: undefined; target?: string; rel?: string });

const TIER_RGB: Record<TierMarkTier, string> = {
  plus: 'var(--ff-tier-plus-rgb)',
  ultra: 'var(--ff-tier-ultra-rgb)',
  founders: 'var(--ff-tier-founders-rgb)',
  torchbearer: 'var(--ff-tier-founders-rgb)',
};

export function PromoCta(props: PromoCtaProps) {
  const {
    children,
    tier = 'ultra',
    loading = false,
    disabled = false,
    variant = 'solid',
    className,
    fullWidth = false,
    'data-testid': dataTestId,
  } = props;
  const style = { '--tier-rgb': TIER_RGB[tier] } as CSSProperties;
  const cls = cx(
    styles.cta,
    styles[variant],
    fullWidth && styles.fullWidth,
    className,
  );
  const content = (
    <>
      <span className={styles.label}>{children}</span>
      <span className={styles.shimmer} aria-hidden="true" />
    </>
  );

  if ('href' in props && props.href) {
    return (
      <a
        className={cls}
        href={props.href}
        style={style}
        data-variant={variant}
        data-tier={tier}
        aria-busy={loading || undefined}
        aria-disabled={disabled || undefined}
        target={'target' in props ? props.target : undefined}
        rel={'rel' in props ? props.rel : undefined}
        data-testid={dataTestId}
      >
        {content}
      </a>
    );
  }

  if ('to' in props && props.to) {
    return (
      <Link
        to={props.to}
        className={cls}
        style={style}
        data-variant={variant}
        data-tier={tier}
        aria-busy={loading || undefined}
        aria-disabled={disabled || undefined}
        data-testid={dataTestId}
      >
        {content}
      </Link>
    );
  }

  const onClick = 'onClick' in props ? props.onClick : undefined;
  return (
    <button
      type="button"
      className={cls}
      style={style}
      data-variant={variant}
      data-tier={tier}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      onClick={onClick}
      data-testid={dataTestId}
    >
      {content}
    </button>
  );
}

export default PromoCta;
